import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { AppEntry, AppFile } from '../api'
import { OLD_ANCHORS, RELAY_LINK, RELAY_STUDIO, STREVIX_OBS, fileKey, fileName, isCli, formatDate, mainFile, osLong, publishedOs, requirement, sortForOs, useApps, type AppsView } from '../apps'
import { AppIcon, AppsAlert, AppsSkeleton, CliBadge, DownloadLink, MineBadge, sizeOf } from '../components/apps-ui'

/** First-run warnings of unsigned builds (docs/apps-downloads.md 2: macOS 15 has no right-click → Open any more). */
const UNSIGNED: Record<string, { label: string; text: string; path: string }> = {
  windows: { label: 'Windows', text: '“Windows protected your PC” →', path: 'More info → Run anyway' },
  macos: { label: 'Mac', text: 'if it says it can’t be opened →', path: 'System Settings → Privacy & Security → Open Anyway' },
}

/** How to run a command line build (docs/wss-ingest.md, "wsbridge"), instead of install steps. */
const CLI_RUN: Record<string, { label: string; steps: string[] }> = {
  macos: {
    label: 'Mac',
    steps: [
      'Unzip it, open Terminal in the wsbridge folder and run ./wsbridge (leave Terminal open).',
      'In OBS → Settings → Stream: Service Custom, Server rtmp://127.0.0.1:1935/live, your usual stream key.',
      'If port 1935 is busy: ./wsbridge -listen 127.0.0.1:19350 and the same port in OBS. If it can’t connect: ./wsbridge -tls 1.2.',
    ],
  },
}

/** Install steps of apps the manifest has none for (docs/apps-downloads.md 7: Strevix for OBS; Strevix Bridge for Mac). */
const DEFAULT_INSTALL: Record<string, { os?: string; text: string }[]> = {
  [RELAY_LINK]: [
    { os: 'macos', text: 'Mac: open the .dmg and drag Strevix Bridge to Applications, then open it. It lives in the menu bar (no Dock icon).' },
    { os: 'macos', text: 'In OBS → Settings → Stream: Service Custom, the server from the Strevix Bridge menu (Copy), your usual stream key.' },
  ],
  [STREVIX_OBS]: [
    { text: 'Needs OBS Studio 30 or newer. Quit OBS before installing.' },
    { os: 'windows', text: 'Windows: run the installer; it puts the plugin into OBS for every user on this computer.' },
    { os: 'macos', text: 'Mac: open the .pkg and follow the installer; it installs for your user, no admin password.' },
    { text: 'Open OBS → the Strevix dock → Sign in, and confirm in your browser. OBS is then set up to stream to Strevix.' },
    { text: 'Press Start Streaming. Your previous stream settings are kept: Tools → Strevix: restore the previous OBS stream settings.' },
  ],
}

const CHECK: Record<string, { cmd: string; on: string }> = {
  macos: { cmd: 'shasum -a 256 <file>', on: 'on Mac' },
  windows: { cmd: 'certutil -hashfile <file> SHA256', on: 'on Windows' },
}

/** /apps: every published build with its size and SHA-256 (artboard Apps-2-Page). */
export function AppsPage() {
  const apps = useApps()
  const m = apps.manifest
  const loc = useLocation()
  const navigate = useNavigate()

  // Old links (/apps#wsbridge) point to the app that replaced it.
  const anchor = decodeURIComponent(loc.hash.slice(1))
  useEffect(() => {
    if (OLD_ANCHORS[anchor]) navigate({ hash: `#${OLD_ANCHORS[anchor]}` }, { replace: true })
  }, [anchor, navigate])

  // #relay-link from "How it works →": scroll once the cards are there.
  useEffect(() => {
    if (apps.state !== 'ready' || !anchor) return
    document.getElementById(anchor)?.scrollIntoView({ block: 'start' })
  }, [apps.state, anchor])

  return (
    <div data-testid="apps-page" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b border-line pb-5">
        <div className="min-w-0">
          {apps.state === 'ready' && m && (
            <span className="text-[13px] text-muted">
              Apps · <span className="font-mono text-text">v{m.version}</span>
              {m.released_at && ` · ${formatDate(m.released_at)}`}
            </span>
          )}
          <h1 className="m-0 text-[30px] font-medium tracking-[-.01em] md:text-[40px]">Get the apps</h1>
        </div>
        {apps.state === 'ready' && m?.notes_url && (
          <a href={m.notes_url} className="text-sm text-accent no-underline hover:text-text">
            What’s new in {m.version} →
          </a>
        )}
      </div>
      <Body apps={apps} />
    </div>
  )
}

function Body({ apps }: { apps: AppsView }) {
  if (apps.state === 'loading' || (apps.state === 'error' && apps.fetching)) {
    return (
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-line p-4"><AppsSkeleton /></div>
        <div className="rounded-lg border border-line p-4 max-md:hidden"><AppsSkeleton /></div>
      </div>
    )
  }
  if (apps.state === 'error') return <div className="max-w-[560px]"><AppsAlert onRetry={apps.retry} /></div>
  if (apps.state === 'none') {
    return (
      <div data-testid="apps-empty" className="rounded-lg border border-dashed border-line-2 p-6">
        <div className="text-lg font-medium">No apps yet</div>
        <p className="m-0 mt-1 text-sm text-text-soft">Downloads appear here once they are published. Until then, stream with OBS.</p>
      </div>
    )
  }
  const oses = publishedOs(apps.apps)
  const relay = apps.app(RELAY_STUDIO)
  return (
    <>
      <div className="grid items-start gap-6 md:grid-cols-2">
        {apps.apps.map((a) => (
          <AppCard key={a.id} app={a} apps={apps} />
        ))}
      </div>
      <p className="m-0 text-[13px] text-faint">
        Check a file:{' '}
        {oses
          .flatMap((o) => (CHECK[o] ? [{ o, ...CHECK[o] }] : []))
          .map((c, i, all) => (
            <span key={c.o}>
              <span className="font-mono">{c.cmd}</span> {c.on}
              {i < all.length - 1 ? ', ' : '.'}
            </span>
          ))}
        {relay && ` ${relay.name} includes FFmpeg (GPL); licenses are inside the download.`}
      </p>
    </>
  )
}

function AppCard({ app, apps }: { app: AppEntry; apps: AppsView }) {
  const has = (os?: string) => !os || app.files.some((f) => f.os === os)
  // A command line build gets how to run it; install steps are for the apps with a window.
  const cliOs = app.files.filter(isCli).map((f) => f.os)
  const guiOs = app.files.filter((f) => !isCli(f)).map((f) => f.os)
  const install = app.install?.length ? app.install : (DEFAULT_INSTALL[app.id] ?? [])
  // An OS gets install steps when it has an app with a window (a Mac may have both).
  const steps = install.filter((s) => s.text && has(s.os) && (s.os ? guiOs.includes(s.os) : guiOs.length > 0))
  const unsigned = Object.entries(UNSIGNED).filter(([os]) => app.files.some((f) => f.os === os && !f.signed))
  const relay = app.id === RELAY_STUDIO
  return (
    <article
      id={app.id}
      aria-labelledby={`app-${app.id}`}
      className="rs-corners flex min-w-0 scroll-mt-6 flex-col gap-3.5 rounded-lg border border-line p-4 md:p-[22px]"
    >
      <div className="flex items-start gap-3.5">
        <span className={`grid h-12 w-12 flex-none place-items-center rounded-md border ${relay ? 'border-accent text-accent' : 'border-line-2 text-text-soft'}`}>
          <AppIcon app={app.id} />
        </span>
        <div className="min-w-0">
          <h2 id={`app-${app.id}`} className="m-0 text-[22px] font-medium">{app.name}</h2>
          <p className="m-0 mt-1 text-text-soft">{app.description || app.summary}</p>
        </div>
      </div>
      <div>
        {sortForOs(app.files, apps.os).map((f) => (
          <FileRow key={f.url} app={app} file={f} mine={f === mainFile(app, apps.os)} />
        ))}
      </div>
      {steps.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="mono-label">INSTALL</span>
          <ol className="m-0 flex flex-col gap-1 pl-5 text-sm text-text-soft">
            {steps.map((s, i) => (
              <li key={i} className="pl-1">{s.text}</li>
            ))}
          </ol>
        </div>
      )}
      {cliOs.map((os) => {
        const run = CLI_RUN[os]
        if (!run) return null
        return (
          <div key={os} data-testid={`apps-cli-${app.id}-${os}`} className="flex flex-col gap-1.5">
            <span className="mono-label">RUN ON {run.label.toUpperCase()} · COMMAND LINE</span>
            <ol className="m-0 flex flex-col gap-1 pl-5 text-sm text-text-soft">
              {run.steps.map((s, i) => (
                <li key={i} className="pl-1">{s}</li>
              ))}
            </ol>
          </div>
        )
      })}
      {unsigned.length > 0 && (
        <div data-testid={`apps-unsigned-${app.id}`} className="rounded-md border border-line-2 px-3 py-2.5 text-[13px] text-muted">
          Not signed yet.{' '}
          {unsigned.map(([os, w]) => (
            <span key={os}>
              {w.label}: {w.text} <b className="font-medium text-text-soft">{w.path}</b>.{' '}
            </span>
          ))}
        </div>
      )}
      {app.note && <div className="rounded-md border border-line-2 px-3 py-2.5 text-[13px] text-muted">{app.note}</div>}
    </article>
  )
}

function FileRow({ app, file, mine }: { app: AppEntry; file: AppFile; mine: boolean }) {
  return (
    <div className="grid grid-cols-1 items-center gap-2.5 border-t border-line py-3 sm:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 font-medium">
          {osLong(file.os)}
          {isCli(file) && <CliBadge testId={`apps-variant-${fileKey(app, file)}`} />}
          {mine && <MineBadge />}
        </div>
        <div className="text-xs text-muted">{requirement(file)}</div>
        <div className="mt-1 flex min-w-0 items-center gap-2 font-mono text-[11px] text-faint">
          <span className="truncate">{fileName(file)}</span>
          {file.sha256 && <ShaButton testId={`apps-sha-${fileKey(app, file)}`} sha={file.sha256} name={fileName(file)} />}
        </div>
      </div>
      <DownloadLink file={file} primary={mine} testId={`apps-download-${fileKey(app, file)}`} className="max-sm:w-full">
        Download · {sizeOf(file)}
      </DownloadLink>
    </div>
  )
}

function ShaButton({ sha, name, testId }: { sha: string; name: string; testId: string }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(t)
  }, [copied])
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        data-copied={copied}
        title={`Copy SHA-256 of ${name}`}
        aria-label={`Copy SHA-256 of ${name}`}
        onClick={async () => {
          await navigator.clipboard?.writeText(sha).catch(() => undefined)
          setCopied(true)
        }}
        className={`flex-none whitespace-nowrap rounded-md border px-1.5 py-0.5 font-mono text-[11px] max-md:min-h-[44px] ${
          copied ? 'border-live/[.45] text-live' : 'border-transparent text-accent hover:text-text'
        }`}
      >
        {copied ? '✓ SHA-256 copied' : 'SHA-256 ⧉'}
      </button>
      <span role="status" className="sr-only">{copied ? 'SHA-256 copied' : ''}</span>
    </>
  )
}

